import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import {
  authKeys,
  initializeKeys,
  orgMemberKeys,
  departmentKeys,
  orgFunctionKeys,
  organizationKeys
} from '../query-keys';
import {
  InitializeAddUsersContract,
  type InitializeAddUsersRequest,
  type InitializeAddUsersResponse
} from '@bliks-io/contracts';

/**
 * Initialize users mutation
 *
 * Creates multiple users in bulk with assigned departments and roles.
 * This triggers automatic migration of temporary departments where the
 * managerEmail matches a created user.
 *
 * - Creates users with department and role assignments
 * - Triggers background service to migrate matching temp departments
 * - Invalidates org members cache
 * - Invalidates temp departments cache (some may be resolved)
 * - Invalidates departments cache (new departments may be created)
 * - Invalidates organization cache (initialization.users stamp flips)
 *
 * @example
 * ```tsx
 * function UsersUploadStep() {
 *   const initializeUsers = useInitializeUsers();
 *
 *   const handleSubmit = async (users: UserInput[]) => {
 *     await initializeUsers.mutateAsync({ users });
 *     toast.success('Users created successfully');
 *   };
 * }
 * ```
 */
export function useInitializeUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InitializeAddUsersRequest): Promise<InitializeAddUsersResponse> => {
      const { data: response } = await apiClient.post<InitializeAddUsersResponse>(
        InitializeAddUsersContract.path,
        data
      );
      return response;
    },
    onSuccess: () =>
      // Awaited (returned) on purpose: the wizard derives step reachability
      // from the organization's initialization stamps (users flips here), so
      // navigation after mutateAsync must see the refetched progress.
      Promise.all([
        queryClient.invalidateQueries({ queryKey: orgMemberKeys.all }),
        // Manager resolution ran inside the import — unresolved departments may have resolved
        queryClient.invalidateQueries({ queryKey: initializeKeys.unresolvedDepartments() }),
        // Creates/updates/removals move the summary's gap counts, which feed
        // the overview's completion-blocked alert.
        queryClient.invalidateQueries({ queryKey: initializeKeys.summary() }),
        queryClient.invalidateQueries({ queryKey: departmentKeys.all }),
        // Role-kind OrgFunctions may have been created during user initialization
        queryClient.invalidateQueries({ queryKey: orgFunctionKeys.all }),
        queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
        // The routing gate reads setup state from the profile cache.
        queryClient.invalidateQueries({ queryKey: authKeys.profile() })
      ])
  });
}
